import type { CSSProperties } from 'react';

import type { LoginLoaderBackgroundStyle } from '@/lib/utils/style';

import {
    LOGIN_LOADER_BG_MODE_ATTRIBUTE,
    STORE_LOADING_COVER_BG_DESKTOP_VAR,
    STORE_LOADING_COVER_BG_MOBILE_VAR,
} from './StoreLoadingCover.consts';

export const LOGIN_LOADER_BG_STORAGE_KEY = 'store-pre-auth-loader-bg';

const toCssUrl = (src?: string): string | undefined => (src ? `url(${src})` : undefined);

/** Mobile + desktop loader URLs, skipping blanks and duplicates. */
export const listLoginLoaderBackgroundUrls = (
    background?: LoginLoaderBackgroundStyle,
): string[] => {
    if (!background) {
        return [];
    }

    const { backgroundImageMobile: mobile, backgroundImageDesktop: desktop } = background;
    const urls: string[] = [];
    if (mobile) {
        urls.push(mobile);
    }
    if (desktop && desktop !== mobile) {
        urls.push(desktop);
    }
    return urls;
};

export const buildLoginLoaderBackgroundHtmlStyle = (
    background?: LoginLoaderBackgroundStyle,
): CSSProperties | undefined => {
    if (!background) {
        return undefined;
    }

    const mobile = toCssUrl(background.backgroundImageMobile);
    const desktop = toCssUrl(background.backgroundImageDesktop);
    if (!mobile && !desktop) {
        return undefined;
    }

    return {
        [STORE_LOADING_COVER_BG_MOBILE_VAR]: mobile,
        [STORE_LOADING_COVER_BG_DESKTOP_VAR]: desktop,
    } as CSSProperties;
};

export const latchLoginLoaderBackground = (
    background: LoginLoaderBackgroundStyle,
): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    if (!background.backgroundImageMobile && !background.backgroundImageDesktop) {
        return false;
    }

    try {
        sessionStorage.setItem(LOGIN_LOADER_BG_STORAGE_KEY, JSON.stringify(background));
        return true;
    } catch {
        return false;
    }
};

export const readLatchedLoginLoaderBackground = (): LoginLoaderBackgroundStyle | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const raw = sessionStorage.getItem(LOGIN_LOADER_BG_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as LoginLoaderBackgroundStyle;
        if (!parsed.backgroundImageMobile && !parsed.backgroundImageDesktop) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
};

export const hasLatchedLoginLoaderBackground = (): boolean =>
    readLatchedLoginLoaderBackground() !== null;

export const clearLatchedLoginLoaderBackground = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        sessionStorage.removeItem(LOGIN_LOADER_BG_STORAGE_KEY);
    } catch {
        // private mode / quota
    }
};

/** Drop latched storefront art when a portal handoff did not reach /shop. */
export const releaseLoginLoaderHandoff = (): void => {
    clearLatchedLoginLoaderBackground();
};

const applyLoginLoaderBackgroundToDocument = (
    bgModeAttr: string,
    mobileVar: string,
    desktopVar: string,
): string =>
    `var root=document.documentElement;root.setAttribute(${bgModeAttr},"1");if(bg.backgroundImageMobile){root.style.setProperty(${mobileVar},"url("+JSON.stringify(bg.backgroundImageMobile)+")");}if(bg.backgroundImageDesktop){root.style.setProperty(${desktopVar},"url("+JSON.stringify(bg.backgroundImageDesktop)+")");}`;

/**
 * Runs synchronously at the top of `<body>`.
 * - Pre-auth login SSR: latch URLs into sessionStorage and paint `<html>`.
 * - Shop hard-nav (and reloads): restore from sessionStorage before first paint.
 */
export const buildLoginLoaderBackgroundBootstrapScript = (
    background?: LoginLoaderBackgroundStyle | null,
): string => {
    const storageKey = JSON.stringify(LOGIN_LOADER_BG_STORAGE_KEY);
    const bgModeAttr = JSON.stringify(LOGIN_LOADER_BG_MODE_ATTRIBUTE);
    const mobileVar = JSON.stringify(STORE_LOADING_COVER_BG_MOBILE_VAR);
    const desktopVar = JSON.stringify(STORE_LOADING_COVER_BG_DESKTOP_VAR);
    const embeddedBg = background ? JSON.stringify(background) : 'null';
    const apply = applyLoginLoaderBackgroundToDocument(bgModeAttr, mobileVar, desktopVar);

    return `(function(){try{var k=${storageKey},embedded=${embeddedBg},raw=embedded?JSON.stringify(embedded):sessionStorage.getItem(k);if(!raw)return;if(embedded){sessionStorage.setItem(k,raw);}var bg=JSON.parse(raw);${apply}}catch(e){}})();`;
};

/** @deprecated Use buildLoginLoaderBackgroundBootstrapScript */
export const buildLoginLoaderBackgroundRestoreScript = (): string =>
    buildLoginLoaderBackgroundBootstrapScript();
